import { Autocomplete, Chip, TextField } from "@mui/material";
import { useMemo } from "react";

import SysColumn from "app/class/sys-column";
import { useChoiceOptions } from "modules/record/shared/hooks/useChoiceOptions";
import { useTranslation } from "react-i18next";

interface InValueFieldProps {
    column: SysColumn;
    value: unknown;
    onChange: (value: any[]) => void;
    disabled?: boolean;
}

const TEXTUAL_IN_TYPES = new Set([
    "UUID",
    "TEXT",
    "DATE",
    "DATETIME",
    "TIME",
    "CHOICE",
    "REFERENCE",
    "DYNAMIC_REFERENCE",
    "PERMISSION_ARRAY",
]);

const NUMERIC_IN_TYPES = new Set([
    "INTEGER",
    "NUMBER",
    "DECIMAL",
]);

const normalizeRawItems = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .flatMap((item) => String(item ?? "").split(","))
        .map((item) => item.trim())
        .filter(Boolean);
};

const normalizeItemByType = (item: string, columnType: string): string | number | null => {
    if (NUMERIC_IN_TYPES.has(columnType)) {
        const parsedNumber = Number(item);
        return Number.isNaN(parsedNumber) ? null : parsedNumber;
    }

    if (TEXTUAL_IN_TYPES.has(columnType)) {
        return item;
    }

    return item;
};

export const InValueField = ({
    column,
    value,
    onChange,
    disabled = false,
}: InValueFieldProps) => {
    const { t } = useTranslation();
    const rawItems = useMemo(() => normalizeRawItems(value), [value]);
    const { data: choiceOptions = [] } = useChoiceOptions(column.column_type === "CHOICE" ? column : undefined);

    const options = useMemo(() => {
        if (column.column_type !== "CHOICE") {
            return [];
        }

        return choiceOptions.map((choice) => choice.value);
    }, [choiceOptions, column.column_type]);

    return (
        <Autocomplete
            multiple
            freeSolo
            options={options}
            value={rawItems}
            onChange={(_, nextValue) => {
                const normalizedItems = nextValue
                    .flatMap((item) => String(item).split(","))
                    .map((item) => item.trim())
                    .filter(Boolean)
                    .map((item) => normalizeItemByType(item, column.column_type))
                    .filter((item): item is string | number => item !== null);

                onChange(Array.from(new Set(normalizedItems)));
            }}
            disabled={disabled}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={`${option}-${index}`}
                        label={option}
                        size="small"
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    placeholder={String(t("Add values"))}
                    helperText={String(t("Use Enter to add values. Comma-separated paste is split automatically."))}
                />
            )}
            sx={{ minWidth: 200, width: "100%" }}
        />
    );
};
